import { motion, AnimatePresence } from "framer-motion";
import { useState } from "react";
import {
  ArrowRight,
  Building2,
  Users,
  TrendingUp,
  Shield,
  Globe,
  CheckCircle2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { properties } from "@/data/properties";

const getCount = (terms: string[]) =>
  properties.filter((p) =>
    terms.some((t) => p.location.toLowerCase().includes(t.toLowerCase()))
  ).length;

interface MarketData {
  id: string;
  name: string;
  flag: string;
  region: string;
  growth: string;
  avgPrice: string;
  propertyCount: number;
  highlights: string[];
  partners: { name: string; specialty: string }[];
  investorBenefits: string[];
  description: string;
}

const markets: MarketData[] = [
  {
    id: "monaco",
    name: "Monaco",
    flag: "🇲🇨",
    region: "Monte Carlo & Larvotto",
    growth: "+12%",
    avgPrice: "€8.5M",
    propertyCount: getCount(["Monaco"]),
    highlights: ["Port Hercules", "Larvotto", "Carré d'Or"],
    partners: [
      { name: "Sotheby's Monaco", specialty: "Waterfront penthouses" },
      { name: "Local luxury boutique", specialty: "Off-market residences" },
    ],
    investorBenefits: ["Zero income tax", "Residency via property", "Safe-haven jurisdiction"],
    description: "The world's most exclusive square mile. Tax efficiency meets unparalleled prestige.",
  },
  {
    id: "dubai",
    name: "Dubai & Abu Dhabi",
    flag: "🇦🇪",
    region: "Palm Jumeirah & Saadiyat",
    growth: "+18%",
    avgPrice: "€3.2M",
    propertyCount: getCount(["Dubai", "Abu Dhabi"]),
    highlights: ["Palm Jumeirah", "Emirates Hills", "Saadiyat Island"],
    partners: [
      { name: "WhiteWill.ae", specialty: "Ultra-prime villas" },
      { name: "Christie's Dubai", specialty: "Branded residences" },
    ],
    investorBenefits: ["Zero income tax", "Golden Visa (€545K+)", "Freehold ownership"],
    description: "The world's fastest-growing luxury market with tax-free returns and golden visa pathways.",
  },
  {
    id: "london",
    name: "London",
    flag: "🇬🇧",
    region: "Mayfair & Knightsbridge",
    growth: "+8%",
    avgPrice: "£12M",
    propertyCount: getCount(["London"]),
    highlights: ["Mayfair", "Knightsbridge", "Chelsea"],
    partners: [
      { name: "Sotheby's London", specialty: "Period townhouses" },
      { name: "Knight Frank", specialty: "New developments" },
    ],
    investorBenefits: ["Stable currency", "Global financial hub", "Strong rental demand"],
    description: "The world's most established luxury market. Timeless value in the global financial capital.",
  },
  {
    id: "italy",
    name: "Italy",
    flag: "🇮🇹",
    region: "Amalfi, Sardinia & Como",
    growth: "+10%",
    avgPrice: "€4.1M",
    propertyCount: getCount(["Italy", "Amalfi", "Sardinia", "Tuscany", "Como"]),
    highlights: ["Amalfi Coast", "Costa Smeralda", "Lake Como"],
    partners: [
      { name: "Italy Sotheby's", specialty: "Historic villas" },
      { name: "Sardinia boutique agency", specialty: "Coastal estates" },
    ],
    investorBenefits: ["Flat tax regime for new residents", "EU residency", "Heritage properties"],
    description: "From cliff-side Amalfi villas to Sardinia's emerald coast — La Dolce Vita as an investment.",
  },
  {
    id: "spain",
    name: "Spain",
    flag: "🇪🇸",
    region: "Marbella & Ibiza",
    growth: "+15%",
    avgPrice: "€2.8M",
    propertyCount: getCount(["Spain", "Marbella", "Ibiza", "Barcelona", "Mallorca"]),
    highlights: ["Golden Mile", "La Zagaleta", "Ibiza"],
    partners: [
      { name: "Christie's Spain", specialty: "Beachfront villas" },
      { name: "Marbella boutique", specialty: "New-build luxury" },
    ],
    investorBenefits: ["Golden Visa (€500K+)", "NHR tax benefits", "Mediterranean lifestyle"],
    description: "Mediterranean living with strong capital growth. Europe's most dynamic luxury corridor.",
  },
  {
    id: "france",
    name: "France",
    flag: "🇫🇷",
    region: "Côte d'Azur & Paris",
    growth: "+9%",
    avgPrice: "€6.5M",
    propertyCount: getCount(["France"]),
    highlights: ["Cap-Ferrat", "Saint-Tropez", "Paris 8th"],
    partners: [
      { name: "Daniel Féau", specialty: "Parisian Haussmann" },
      { name: "Riviera specialists", specialty: "Waterfront estates" },
    ],
    investorBenefits: ["EU gateway", "Cultural capital", "Strong rental yields in Paris"],
    description: "From Haussmann grandeur to sun-drenched Riviera estates — the apex of European refinement.",
  },
  {
    id: "hong-kong",
    name: "Hong Kong & Macau",
    flag: "🇭🇰",
    region: "The Peak & Cotai",
    growth: "+6%",
    avgPrice: "HK$180M",
    propertyCount: getCount(["Hong Kong", "Macau"]),
    highlights: ["The Peak", "Repulse Bay", "Coloane"],
    partners: [
      { name: "Sotheby's Asia", specialty: "Hilltop estates" },
      { name: "HK luxury boutique", specialty: "New developments" },
    ],
    investorBenefits: ["Asia's financial hub", "Low tax regime", "Strategic gateway to China"],
    description: "Asia's premier addresses. Unmatched density of ultra-wealth and financial infrastructure.",
  },
  {
    id: "usa",
    name: "USA",
    flag: "🇺🇸",
    region: "NYC, LA & Miami",
    growth: "+10%",
    avgPrice: "$18M",
    propertyCount: getCount(["USA", "New York", "Malibu", "Beverly Hills"]),
    highlights: ["Manhattan", "Beverly Hills", "Palm Beach"],
    partners: [
      { name: "Sotheby's NYC", specialty: "Trophy penthouses" },
      { name: "The Agency LA", specialty: "Celebrity estates" },
    ],
    investorBenefits: ["World's largest economy", "EB-5 visa pathway", "Dollar-denominated assets"],
    description: "From Manhattan skylines to Malibu coastlines — the world's deepest luxury market.",
  },
  {
    id: "ksa",
    name: "Saudi Arabia",
    flag: "🇸🇦",
    region: "Riyadh & NEOM",
    growth: "+22%",
    avgPrice: "SAR 35M",
    propertyCount: getCount(["Saudi Arabia", "KSA", "Riyadh"]),
    highlights: ["Riyadh", "NEOM", "Red Sea"],
    partners: [
      { name: "KSA premium agency", specialty: "Royal-grade palaces" },
      { name: "NEOM specialists", specialty: "Future developments" },
    ],
    investorBenefits: ["Vision 2030 growth", "Zero income tax", "Emerging luxury hub"],
    description: "The Kingdom's transformation is creating unprecedented investment opportunities.",
  },
  {
    id: "switzerland",
    name: "Switzerland",
    flag: "🇨🇭",
    region: "Geneva & St. Moritz",
    growth: "+7%",
    avgPrice: "CHF 12M",
    propertyCount: getCount(["Switzerland"]),
    highlights: ["Geneva", "Gstaad", "St. Moritz"],
    partners: [
      { name: "Swiss premium realty", specialty: "Lakefront properties" },
      { name: "Alpine specialists", specialty: "Ski chalets" },
    ],
    investorBenefits: ["Political neutrality", "Banking infrastructure", "Lump-sum taxation"],
    description: "Alpine prestige meets absolute privacy. The world's most discreet luxury market.",
  },
  {
    id: "australia",
    name: "Australia",
    flag: "🇦🇺",
    region: "Sydney & Gold Coast",
    growth: "+11%",
    avgPrice: "A$15M",
    propertyCount: getCount(["Australia"]),
    highlights: ["Sydney Harbour", "Melbourne", "Byron Bay"],
    partners: [
      { name: "Sotheby's Australia", specialty: "Harbourside mansions" },
      { name: "Gold Coast boutique", specialty: "Beachfront estates" },
    ],
    investorBenefits: ["Stable democracy", "Strong dollar", "Lifestyle premium"],
    description: "Harbour-side mansions and coastal retreats in the Southern Hemisphere's crown jewels.",
  },
  {
    id: "qatar",
    name: "Qatar",
    flag: "🇶🇦",
    region: "The Pearl & Lusail",
    growth: "+16%",
    avgPrice: "QAR 18M",
    propertyCount: getCount(["Qatar", "Doha"]),
    highlights: ["The Pearl", "Lusail", "West Bay"],
    partners: [
      { name: "Qatar luxury agency", specialty: "The Pearl residences" },
      { name: "Lusail specialists", specialty: "Branded developments" },
    ],
    investorBenefits: ["Zero income tax", "Residency via property", "Post-World Cup infrastructure"],
    description: "Post-World Cup renaissance with extraordinary architectural ambition and tax efficiency.",
  },
];

const MarketPartnerReveal = () => {
  const [selectedMarket, setSelectedMarket] = useState<string>("monaco");
  const activeMarket = markets.find((m) => m.id === selectedMarket) || markets[0];

  return (
    <section id="markets" className="py-24 lg:py-32 bg-secondary overflow-hidden">
      <div className="container mx-auto px-6 lg:px-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-16 lg:mb-20"
        >
          <p className="font-body text-sm uppercase tracking-[0.3em] text-primary mb-4">
            Select Your Market
          </p>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-light text-foreground max-w-3xl mb-6">
            Choose a Destination.
            <span className="block italic font-medium text-primary">Meet Your Local Team.</span>
          </h2>
          <p className="font-body text-lg text-muted-foreground max-w-2xl">
            We don't just list properties — we match you with the best local brokerage in each market, 
            ensuring intimate expertise and on-the-ground guidance at every step.
          </p>
        </motion.div>

        {/* Market selector + Detail panel */}
        <div className="grid lg:grid-cols-12 gap-8">
          {/* Market pills / list */}
          <div className="lg:col-span-4">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-1 gap-2">
              {markets.map((market) => (
                <button
                  key={market.id}
                  onClick={() => setSelectedMarket(market.id)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl text-left transition-all duration-300 ${
                    market.id === selectedMarket
                      ? "bg-primary/10 border border-primary/30 shadow-sm"
                      : "bg-background/50 border border-transparent hover:bg-background hover:border-border/50"
                  }`}
                >
                  <span className="text-lg">{market.flag}</span>
                  <div className="min-w-0">
                    <p
                      className={`font-display text-sm truncate ${
                        market.id === selectedMarket
                          ? "text-primary font-medium"
                          : "text-foreground"
                      }`}
                    >
                      {market.name}
                    </p>
                    <p className="text-[10px] text-muted-foreground truncate">
                      {market.propertyCount} properties
                    </p>
                  </div>
                  {market.id === selectedMarket && (
                    <div className="ml-auto flex items-center text-primary">
                      <TrendingUp className="h-3.5 w-3.5" />
                      <span className="text-xs font-medium ml-1">{market.growth}</span>
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Detail panel */}
          <div className="lg:col-span-8">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeMarket.id}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
                className="bg-card border border-border/50 rounded-2xl overflow-hidden"
              >
                {/* Market header */}
                <div className="p-8 lg:p-10 border-b border-border/30">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-3xl">{activeMarket.flag}</span>
                        <div>
                          <h3 className="font-display text-2xl lg:text-3xl text-foreground">
                            {activeMarket.name}
                          </h3>
                          <p className="font-body text-sm text-muted-foreground">
                            {activeMarket.region}
                          </p>
                        </div>
                      </div>
                      <p className="text-muted-foreground leading-relaxed mt-3 max-w-lg">
                        {activeMarket.description}
                      </p>
                    </div>
                    <div className="hidden sm:flex items-center gap-1 text-primary bg-primary/10 px-3 py-1.5 rounded-full">
                      <TrendingUp className="h-4 w-4" />
                      <span className="text-sm font-medium">{activeMarket.growth} YoY</span>
                    </div>
                  </div>

                  {/* Quick stats */}
                  <div className="grid grid-cols-3 gap-4 mt-6">
                    <div className="bg-background/60 rounded-lg p-3">
                      <p className="text-xs text-muted-foreground">Avg. Price</p>
                      <p className="font-display text-lg text-primary font-semibold">
                        {activeMarket.avgPrice}
                      </p>
                    </div>
                    <div className="bg-background/60 rounded-lg p-3">
                      <p className="text-xs text-muted-foreground">Properties</p>
                      <p className="font-display text-lg text-foreground font-semibold">
                        {activeMarket.propertyCount}
                      </p>
                    </div>
                    <div className="bg-background/60 rounded-lg p-3">
                      <p className="text-xs text-muted-foreground">Key Areas</p>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {activeMarket.highlights.slice(0, 2).map((h) => (
                          <span
                            key={h}
                            className="text-[10px] bg-primary/10 text-primary px-2 py-0.5 rounded-full"
                          >
                            {h}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Partner reveal */}
                <div className="p-8 lg:p-10 bg-primary/[0.03]">
                  <div className="grid md:grid-cols-2 gap-8">
                    {/* Local partners */}
                    <div>
                      <div className="flex items-center gap-2 mb-4">
                        <Users className="h-4 w-4 text-primary" />
                        <h4 className="font-body text-sm uppercase tracking-[0.15em] text-primary font-medium">
                          Your Local Team
                        </h4>
                      </div>
                      <div className="space-y-3">
                        {activeMarket.partners.map((partner) => (
                          <div
                            key={partner.name}
                            className="flex items-center gap-3 bg-background/80 border border-border/30 rounded-lg p-3"
                          >
                            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                              <Building2 className="h-5 w-5 text-primary" />
                            </div>
                            <div>
                              <p className="font-medium text-sm text-foreground">
                                {partner.name}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {partner.specialty}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Investor benefits */}
                    <div>
                      <div className="flex items-center gap-2 mb-4">
                        <Shield className="h-4 w-4 text-primary" />
                        <h4 className="font-body text-sm uppercase tracking-[0.15em] text-primary font-medium">
                          Investor Benefits
                        </h4>
                      </div>
                      <ul className="space-y-3">
                        {activeMarket.investorBenefits.map((benefit) => (
                          <li key={benefit} className="flex items-center gap-3">
                            <CheckCircle2 className="h-4 w-4 text-primary flex-shrink-0" />
                            <span className="text-sm text-foreground">{benefit}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  {/* CTA */}
                  <div className="flex flex-col sm:flex-row items-center gap-3 mt-8 pt-6 border-t border-border/30">
                    <Button variant="luxury" size="lg" asChild className="w-full sm:w-auto">
                      <Link to={`/properties?location=${encodeURIComponent(activeMarket.name)}`}>
                        View {activeMarket.name} Properties
                        <ArrowRight className="h-4 w-4 ml-2" />
                      </Link>
                    </Button>
                    <Button variant="outline" size="lg" asChild className="w-full sm:w-auto">
                      <a href="#investment-assessment">
                        <Globe className="h-4 w-4 mr-2" />
                        Get Matched with an Advisor
                      </a>
                    </Button>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
};

export default MarketPartnerReveal;
